import { ArrowLeft, FolderOpen, HardDrive } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { isElectron } from "@/services/storage/LocalFileSystemClient";

const UserPreferences = () => {
  const { toast } = useToast();
  const isMacElectron = isElectron() && window.electronAPI?.platform === "darwin";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [effectivePath, setEffectivePath] = useState<string>("");
  const [defaultPath, setDefaultPath] = useState<string>("");
  const [isCustom, setIsCustom] = useState(false);

  const loadLocation = useCallback(async () => {
    if (!isElectron() || !window.electronAPI?.getStorageLocation) {
      setLoading(false);
      return;
    }
    try {
      const loc = await window.electronAPI.getStorageLocation();
      setEffectivePath(loc.effectivePath);
      setDefaultPath(loc.defaultPath);
      setIsCustom(loc.isCustom);
    } catch {
      toast({
        variant: "destructive",
        title: "Could not load storage settings",
        description: "Try again or restart the desktop app.",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadLocation();
  }, [loadLocation]);

  const handleChooseFolder = useCallback(async () => {
    if (!isElectron() || !window.electronAPI) return;
    setSaving(true);
    try {
      const picked = await window.electronAPI.openDirectoryDialog({
        title: "Choose folder for panorama file storage",
        defaultPath: effectivePath || undefined,
      });
      if (!picked) return;
      toast({
        title: "Moving storage…",
        description: "Copying files, verifying, then switching. This may take a while.",
      });
      await window.electronAPI.setCustomStorageRoot(picked);
      toast({
        title: "Storage location updated",
        description: "Reloading so the app uses the new path.",
      });
      window.location.reload();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Could not set storage folder",
        description: error instanceof Error ? error.message : "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  }, [effectivePath, toast]);

  const handleUseDefault = useCallback(async () => {
    if (!isElectron() || !window.electronAPI) return;
    setSaving(true);
    try {
      toast({
        title: "Moving storage to default…",
        description: "Copying files, verifying, then switching. This may take a while.",
      });
      await window.electronAPI.resetStorageLocation();
      toast({
        title: "Using default storage",
        description: "Reloading so the app uses the default folder.",
      });
      window.location.reload();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Could not reset storage location",
        description: error instanceof Error ? error.message : "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  }, [toast]);

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <header className="border-b border-gray-700 px-4 py-4 flex items-center gap-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`text-gray-300 hover:text-white hover:bg-gray-800 ${isMacElectron ? "ml-14" : ""}`}
          asChild
        >
          <Link to="/dashboard" className="flex items-center gap-2">
            <ArrowLeft size={16} />
            Back to dashboard
          </Link>
        </Button>
        <h1 className="text-lg font-semibold">User preferences</h1>
      </header>
      <main className="flex-1 p-6 max-w-2xl">
        {!isElectron() && (
          <p className="text-sm text-gray-400">
            Storage location is only configurable in the desktop (Electron) app.
          </p>
        )}
        {isElectron() && (
          <Card className="bg-gray-800/80 border-gray-700 text-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <HardDrive size={18} className="text-gray-300" />
                Local file storage
              </CardTitle>
              <CardDescription className="text-gray-400 text-sm">
                Panorama and media files are stored on disk at this folder. Project databases stay in the
                application data directory; only file storage moves here.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : (
                <>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                      Active storage folder
                    </p>
                    <p className="text-sm font-mono text-gray-200 break-all rounded border border-gray-600 bg-gray-900/60 px-3 py-2">
                      {effectivePath || "—"}
                    </p>
                    {isCustom ? (
                      <p className="text-xs text-emerald-400/90 mt-1">Using a custom location.</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">Using the default location.</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                      Default (app data)
                    </p>
                    <p className="text-xs font-mono text-gray-500 break-all">{defaultPath}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      type="button"
                      variant="secondary"
                      className="bg-gray-700 text-white hover:bg-gray-600"
                      disabled={saving}
                      onClick={handleChooseFolder}
                    >
                      <FolderOpen size={16} className="mr-2" />
                      Choose folder…
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-gray-600 text-gray-200 hover:bg-gray-700"
                      disabled={saving || !isCustom}
                      onClick={handleUseDefault}
                      title={
                        isCustom
                          ? "Copy files into the default app storage folder, verify, then clear the custom folder"
                          : undefined
                      }
                    >
                      Use default location
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Choosing a custom folder copies your files there, verifies them, then empties the previous
                    storage folder (the folder stays).{" "}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default UserPreferences;
